import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Orders, Public, getAuth } from "@/lib/api";
import { AlertCircle, CheckCircle2, CreditCard, Sparkles } from "lucide-react";

type Provider = "stripe" | "alipay" | "wechat";

const PROVIDER_META: Record<Provider, { label: string; tone: string }> = {
  stripe: { label: "Stripe / 信用卡", tone: "bg-indigo-600 hover:bg-indigo-700 text-white" },
  alipay: { label: "支付宝", tone: "bg-sky-600 hover:bg-sky-700 text-white" },
  wechat: { label: "微信支付", tone: "bg-emerald-600 hover:bg-emerald-700 text-white" },
};

export function PricingPage() {
  const [plans, setPlans] = useState<any[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingQR, setPendingQR] = useState<{ url: string; orderId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([Orders.plans(), Public.siteConfig().catch(() => ({}))])
      .then(([ps, site]: any[]) => {
        setPlans(ps);
        const cfg = site?.["payment.providers"] || {};
        const enabled: Provider[] = (Object.keys(cfg) as Provider[]).filter(
          (k) => cfg[k] && cfg[k].enabled !== false && (k === "stripe" || k === "alipay" || k === "wechat"),
        );
        setProviders(enabled);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function buy(plan_code: string, provider: Provider) {
    if (!getAuth()) {
      location.hash = "#/login?next=/pricing";
      return;
    }
    setError(null);
    try {
      const r = await Orders.create({ kind: "membership", plan_code, provider });
      if (r.redirect_url) location.href = r.redirect_url;
      else if (r.qr_code_url) setPendingQR({ url: r.qr_code_url, orderId: r.order_id });
    } catch (e: any) { setError(e.message); }
  }

  if (loading) return <div className="container mx-auto px-4 py-12">加载中…</div>;

  return (
    <div className="container mx-auto px-4 py-10 max-w-6xl">
      <div className="text-center max-w-2xl mx-auto space-y-3">
        <span className="inline-flex items-center gap-1 text-xs rounded-full bg-primary/10 text-primary px-3 py-1">
          <Sparkles className="h-3 w-3" /> 会员订阅
        </span>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">解锁全部招投中标信息</h1>
        <p className="text-muted-foreground">订阅后可查看完整公告内容、中标详情、附件下载，并享高频更新与导出能力。</p>
      </div>

      {error && (
        <div className="mt-6 max-w-xl mx-auto flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 mt-0.5" />{error}
        </div>
      )}

      {providers.length === 0 && (
        <div className="mt-6 max-w-xl mx-auto flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <div>
            <div className="font-medium">暂未开启在线支付</div>
            <div>管理员尚未配置支付渠道，请联系网站客服线下购买，或前往后台「站点设置 - 支付渠道」启用。</div>
          </div>
        </div>
      )}

      <div className="mt-10 grid gap-5 md:grid-cols-3">
        {plans.map((p, idx) => {
          const recommended = idx === 1;
          return (
            <Card key={p.id} className={`flex flex-col relative ${recommended ? "border-primary shadow-lg shadow-primary/10 md:scale-[1.03]" : ""}`}>
              {recommended && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary text-primary-foreground text-xs px-3 py-0.5">推荐</span>
              )}
              <CardHeader>
                <CardTitle className="text-xl">{p.name}</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col gap-3">
                <div className="text-3xl font-bold">
                  ¥{(p.price_cents / 100).toFixed(0)}
                  <span className="text-sm font-normal text-muted-foreground ml-1">/{p.period === "yearly" ? "年" : p.period === "monthly" ? "月" : p.period}</span>
                </div>
                {p.description && <p className="text-sm text-muted-foreground">{p.description}</p>}
                {Array.isArray(p.benefits?.items) && (
                  <ul className="text-sm space-y-1.5 mt-2">
                    {p.benefits.items.map((b: string, i: number) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-auto flex flex-col gap-2 pt-4">
                  {providers.length === 0 ? (
                    <Button disabled variant="outline" className="w-full">
                      <CreditCard className="h-4 w-4 mr-1" /> 暂无可用支付方式
                    </Button>
                  ) : (
                    providers.map((pr) => (
                      <Button key={pr} onClick={() => buy(p.code, pr)} className={PROVIDER_META[pr].tone}>
                        {PROVIDER_META[pr].label}
                      </Button>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {plans.length === 0 && <p className="text-muted-foreground col-span-3 text-center py-8">暂无可购买的套餐，请联系管理员配置。</p>}
      </div>

      {pendingQR && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setPendingQR(null)}>
          <Card className="max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <CardHeader><CardTitle>请使用微信扫码支付</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-center">
              <img alt="qr" className="mx-auto" width={220} height={220}
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(pendingQR.url)}`} />
              <p className="text-xs text-muted-foreground break-all">{pendingQR.url}</p>
              <p className="text-xs text-muted-foreground">订单号：{pendingQR.orderId}</p>
              <Button variant="outline" onClick={() => setPendingQR(null)}>关闭</Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
